import { action, computed, observable, reaction } from 'mobx';
import { DateTime } from 'luxon';

import httpFacade from 'http/httpFacade';
import { IntervalFetch } from 'http/intervalFetch';
import Log from 'helpers/log';
import { getDate, getLocalDateTime } from 'helpers/datetime';
import {
  ConsumptionCondition,
  OrderModel,
  OrderState,
  SwipeDirection,
} from '../UserProfile/types';
import {
  OnlineOrderGroup,
  OnlineOrderPlace,
  OnlineOrdersFilters,
  OnlineOrderStatus,
  OrderLocation,
} from './type';
import { MOBILE_DEVICES } from '../constants';
import {
  NewOnlineOrdersData,
  SocketDataType,
  DepositItemsRequestConfirmData,
  DepositItemsRequestCompleteData,
  OrderCancelData,
  SocketData,
  OrderRequestToCancelData,
  OrderCancelDeclinedData,
} from 'stores/Socket/types';
import { sortByAccessor } from 'helpers/accessors';
import { PromiseService } from 'services/PromiseService';

class OnlineOrdersStore {
  @observable searchValue: string = '';
  @observable currentDate: DateTime = getLocalDateTime();
  @observable activeDate: DateTime = getLocalDateTime();
  @observable intervalFetch: IntervalFetch = new IntervalFetch();
  @observable locations: OrderLocation[] = [];
  @observable depositItemsConfirmation = new Map<string, string>();
  @observable filters: OnlineOrdersFilters = {
    places: [OnlineOrderPlace.ALL],
    statuses: [OnlineOrderStatus.PENDING],
  };
  @observable private _orders: OrderModel[] = [];
  @observable private _markRowsTimeout: ReturnType<
    typeof setTimeout
  > | null = null;
  @observable private _promiseService = new PromiseService();

  private _vorwerkOnlineOrdersSort = sortByAccessor({
    accessor: 'queueNumber',
    desc: false,
  });

  constructor() {
    this.intervalFetch.setRequest(this.fetchOrders);

    reaction(
      () => this.activeDate,
      async () => {
        await this._promiseService.add(this.intervalFetch.fetch);
      },
    );
  }

  @action.bound
  async init() {
    try {
      [{ data: this.locations }] = await this._promiseService.add(async () =>
        Promise.all([
          await httpFacade.onlineOrders.fetchOrderLocations(),
          await this.intervalFetch.start(),
        ]),
      );
      this.fetchDepositItemsRequests();
    } catch (error) {
      Log.warn(error);
    }
  }

  @action.bound
  applyFilter(filters: OnlineOrdersFilters) {
    this.filters = filters;
  }

  @action.bound
  async fetchOrders() {
    try {
      const requestDate = getDate(this.activeDate.toJSDate());
      const { data } = await httpFacade.onlineOrders.fetchOnlineOrders({
        from: requestDate,
        to: requestDate,
      });

      this._orders = this._getOrdersFromGroups(data);

      this.markTableRows();
    } catch (error) {
      Log.warn(error);
    }
  }

  @action.bound
  async fetchOrdersAfterDate(after: string) {
    try {
      const requestDate = getDate(this.activeDate.toJSDate());
      const { data } = await httpFacade.onlineOrders.fetchOnlineOrders({
        from: requestDate,
        to: requestDate,
        after,
      });

      const newOrders = this._getOrdersFromGroups(data);

      const map = new Map<string, OrderModel>();

      this._orders.forEach(order => map.set(order.id, order));
      newOrders.forEach(order => map.set(order.id, order));

      this._orders = Array.from(map.values());

      this.markTableRows();
    } catch (error) {
      Log.warn(error);
    }
  }

  @action
  searchOrder(value: string) {
    this.searchValue = value;
  }

  @action.bound
  changeDate(date: any) {
    this.activeDate = date;
  }

  @action.bound
  swipeDay(value: SwipeDirection) {
    this.activeDate =
      value === 'previous'
        ? this.activeDate.minus({ day: 1 })
        : this.activeDate.plus({ day: 1 });
  }

  @action.bound
  clearInterval() {
    this.intervalFetch.stop();
  }

  @action.bound
  async subscriptionHandler(event) {
    const eventData: SocketData = JSON.parse(event.data);
    if (!eventData?.type) return;

    switch (eventData.type) {
      case SocketDataType.NEW_ONLINE_ORDERS: {
        const newOrdersData = eventData as NewOnlineOrdersData;

        await this.fetchOrdersAfterDate(newOrdersData.payload.lastCreatedDate);

        break;
      }

      case SocketDataType.DEPOSIT_ITEMS_REQUEST_CONFIRM: {
        const depositItemsRequest = eventData as DepositItemsRequestConfirmData;

        this.depositItemsConfirmation.set(
          depositItemsRequest.payload.deviceName,
          depositItemsRequest.payload.pin,
        );

        break;
      }

      case SocketDataType.DEPOSIT_ITEMS_REQUEST_COMPLETED: {
        const depositItemsComplete = eventData as DepositItemsRequestCompleteData;

        this.depositItemsConfirmation.delete(
          depositItemsComplete.payload.deviceName,
        );

        break;
      }

      case SocketDataType.ORDER_CANCELLED: {
        const { orderId } = (eventData as OrderCancelData).payload;
        this.setOrderState(orderId, OrderState.CANCELLED);

        break;
      }

      case SocketDataType.ORDER_REQUEST_TO_CANCEL: {
        const { orderId } = (eventData as OrderRequestToCancelData).payload;
        this.setOrderState(orderId, OrderState.CANCEL_REQUESTED);

        break;
      }

      case SocketDataType.ORDER_CANCEL_DECLINED: {
        const { orderId } = (eventData as OrderCancelDeclinedData).payload;
        this.setOrderState(orderId, OrderState.DECLINE_CANCEL);

        break;
      }
    }
  }

  @action.bound
  markTableRows() {
    if (this._markRowsTimeout) {
      clearTimeout(this._markRowsTimeout);
    }

    this._markRowsTimeout = setTimeout(this.markTableRows, 60000);

    this._orders = this._orders.map(order => {
      order.recentOrder = this._getIsRecentOrder(order);

      return order;
    });
  }

  @action.bound
  updateOrderStatus(id: string) {
    this._orders = this._orders.map(order => {
      if (order.id === id) {
        order.orderProcessingFinished = true;
      }

      return order;
    });

    this.markTableRows();
  }

  @action.bound
  setOrderState(id: string, state: OrderState) {
    this._orders = this._orders.map(order => {
      if (order.id === id) order.state = state;

      return order;
    });

    this.markTableRows();
  }

  @action.bound
  async fetchDepositItemsRequests() {
    try {
      const {
        data,
      } = await httpFacade.onlineOrders.fetchDepositItemsRequests();
      this.depositItemsConfirmation.clear();
      for (const request in data) {
        if (data.hasOwnProperty(request)) {
          this.depositItemsConfirmation.set(request, data[request]);
        }
      }
    } catch (error) {
      Log.warn(error);
    }
  }

  private _defaultOnlineOrdersSort = (a: OrderModel, b: OrderModel): number => {
    if (a.orderProcessingFinished === b.orderProcessingFinished) {
      return a.queueNumber - b.queueNumber;
    } else {
      return a.orderProcessingFinished ? 1 : -1;
    }
  };

  private _getOrdersFromGroups(orderGroups: OnlineOrderGroup[]) {
    return orderGroups
      .map(items => {
        items.orders.map(it => {
          it.deliveryTimeSlot = items.deliveryTimeSlot;
          return it;
        });
        return items;
      })
      .map(group => group.orders)
      .flat();
  }

  private _getIsRecentOrder(order: OrderModel) {
    return (
      !order.orderProcessingFinished &&
      order.state !== OrderState.CANCELLED &&
      DateTime.fromISO(order.createdDate, { zone: 'utc' }).diffNow('seconds')
        .seconds *
        -1 <
        60
    );
  }

  private _getSortedOrders(orders: OrderModel[]) {
    const isVorwerk = window.location.hostname.includes('vorwerk');

    return [...orders].sort(
      isVorwerk ? this._vorwerkOnlineOrdersSort : this._defaultOnlineOrdersSort,
    );
  }

  private _getFilteredOrders(
    orders: OrderModel[],
    filters: OnlineOrdersFilters,
  ) {
    return [...orders].filter(order => {
      const matchesPlace =
        !filters.places.length ||
        filters.places.includes(OnlineOrderPlace.ALL) ||
        filters.places.includes(order.deliveryLocation?.id || '') ||
        (filters.places.includes(OnlineOrderPlace.TAKE_AWAY) &&
          order.consumptionCondition === ConsumptionCondition.TAKE_AWAY) ||
        (filters.places.includes(OnlineOrderPlace.IN_PLACE) &&
          order.consumptionCondition === ConsumptionCondition.IN_PLACE) ||
        (filters.places.includes(OnlineOrderPlace.DELIVERY) &&
          (order.deliveryLocation || order.deliveryInfo?.pointOfDelivery));

      if (!matchesPlace) return false;

      const isCancelled = order.state === OrderState.CANCELLED;
      const matchesStatus =
        !filters.statuses.length ||
        filters.statuses.includes(OnlineOrderStatus.ALL) ||
        (filters.statuses.includes(OnlineOrderStatus.DELIVERED) &&
          order.orderProcessingFinished &&
          !isCancelled) ||
        (filters.statuses.includes(OnlineOrderStatus.PENDING) &&
          !order.orderProcessingFinished &&
          !isCancelled) ||
        (filters.statuses.includes(OnlineOrderStatus.CANCELLED) &&
          order.state === OrderState.CANCELLED);

      if (!matchesStatus) return false;

      const matchesSearchValue = this.searchValue
        ? this.regExpQueNumber.test(String(order.queueNumber)) ||
          order.items.some(item => this.regExpItemTitle.test(item.title))
        : true;

      return matchesSearchValue;
    });
  }

  @computed
  get orders() {
    const filteredOrders = this._getFilteredOrders(this._orders, this.filters);
    const sortedOrders = this._getSortedOrders(filteredOrders);

    return sortedOrders;
  }

  @computed
  get regExpItemTitle() {
    return new RegExp(this.searchValue, 'i');
  }

  @computed
  get orderLocations() {
    const formattedlocations = this.locations.map(location => {
      return { value: location.id, label: location.title };
    });
    return [
      {
        value: '',
        label: '----------------',
        disabled: true,
      },
      ...formattedlocations,
    ];
  }

  @computed
  get regExpQueNumber() {
    return new RegExp(this.searchValue, 'i');
  }

  @computed
  get isCurrentDay() {
    return this.activeDate.toISODate() === this.currentDate.toISODate();
  }

  @computed
  get isMobileOrTablet() {
    const devices = new RegExp(MOBILE_DEVICES, 'i');
    const msStream = 'MSStream';
    const isIOS =
      (/iPad|iPhone|iPod/.test(navigator.platform) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      (!window.hasOwnProperty(msStream) || !window[msStream]);

    return devices.test(navigator.userAgent) || isIOS;
  }

  @computed
  get loading() {
    return this._promiseService.isPending;
  }
}

export default OnlineOrdersStore;
